import { isZeptoLineItem, zeptoChildLineItem } from './zepto';
// Shared helpers for Pressing Portal board + detail view.
// Extracted from PressingPortal.jsx so they can be reused by PressingOrderBoard
// and BundleDetail.
//
// Press recipes: the backend (shopify_client.py) fetches a Shopify metaobject
// at metafield `custom.pressing_instructions` on the line item's *product*,
// flattens its fields, and attaches the result as `lineItem.pressingInstructions`.
// Shape verified against real order SP-243207 (6895984640178):
//   lineItem.pressingInstructions = {
//     title:    "613 Digital Hybrid",   // recipe preset name
//     temp:     "310°",
//     time:     "10sec",
//     pressure: "Heavy",
//     peel:     "Cold"
//   }
// When no metaobject is set, pressingInstructions is null/undefined.

export const EMBELLISHMENT_TYPES = new Set(['Logo', 'LOGO', 'Name', 'Number', 'Embroidered Logo', 'Embroidered Number', 'Crest', 'Decals']);

// An item is a "press-required optional embellishment" iff it has the
// `_soccer_parent_line_item_key` custom attribute. This attribute explicitly
// links an embellishment to its parent garment and marks it as an optional
// add-on the customer chose (vs. a Shopify-native bundle child, which is
// pre-applied at the factory and doesn't need pressing).
//
// Items with `_soccer_club_product_distribution = optional` are the same set —
// we check the parent_line_item_key because it's the linking mechanism and
// suffices on its own.
export function isOptionalEmbellishment(lineItem) {
    if (!lineItem) return false;
    const attrs = lineItem.customAttributes || [];
    return attrs.some(a => a.key === '_soccer_parent_line_item_key' && a.value);
}

// Missing-image placeholder — grey SVG with "Missing Image" diagonally across.
// Same shape as the one used in OrderDetail so the two views look consistent.
export const MISSING_IMAGE_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">' +
    '<rect width="200" height="200" fill="#d1d5db"/>' +
    '<text x="100" y="100" font-family="-apple-system,Segoe UI,sans-serif" font-size="22" font-weight="700" fill="#6b7280" text-anchor="middle" dominant-baseline="middle" transform="rotate(-45 100 100)">Missing Image</text>' +
    '</svg>'
);

// onError handler for <img> — swap the broken image for the placeholder.
// Guards against an infinite loop if the placeholder itself ever errors.
// 2026-08-03: retry twice with backoff before falling back to the missing-
// image SVG. iPad + flaky Shopify CDN was showing the red-X placeholder for
// backpacks and other product images on first packing-detail open; the
// image loaded fine on the very next scan. Two quick retries (250ms, 500ms)
// masks that entirely without the user seeing a placeholder.
export const handleMissingImage = (e) => {
    const el = e.currentTarget;
    if (!el || el.src === MISSING_IMAGE_SVG) return;
    const attempt = parseInt(el.dataset.retryCount || '0', 10);
    if (attempt < 2) {
        el.dataset.retryCount = String(attempt + 1);
        if (!el.dataset.originalSrc) el.dataset.originalSrc = el.src;
        const originalSrc = el.dataset.originalSrc;
        // Cache-buster on the URL avoids replaying a poisoned browser or CDN
        // cache entry (the transient failure often IS the cached record).
        const sep = originalSrc.includes('?') ? '&' : '?';
        setTimeout(() => {
            if (el && el.isConnected) el.src = `${originalSrc}${sep}_r=${attempt + 1}`;
        }, 250 * (attempt + 1));
        return;
    }
    el.onerror = null;
    el.src = MISSING_IMAGE_SVG;
};

// onLoad handler for <img> — samples the actual image pixels and applies a
// grey backgroundColor to the element if >90% of non-transparent pixels are
// near-white. Matches SmartProductImage's logic so Pressing Portal previews
// show white garments visibly (same as the order view).
//
// Usage:
//   <img src={...} onLoad={handleWhiteBgDetection} onError={handleMissingImage} />
export const handleWhiteBgDetection = (e) => {
    try {
        const img = e.currentTarget;
        if (img.dataset.whiteChecked === '1') return;
        img.dataset.whiteChecked = '1';
        const canvas = document.createElement('canvas');
        canvas.width = 20; canvas.height = 20;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 20, 20);
        const { data } = ctx.getImageData(0, 0, 20, 20);
        let product = 0, lightProduct = 0;
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
            if (a < 30) continue; // skip transparent background
            product++;
            if (r > 210 && g > 210 && b > 210) lightProduct++;
        }
        if (product > 0 && lightProduct / product > 0.90) {
            img.style.backgroundColor = '#9ca3af';
        }
    } catch (_) {
        // CORS taint — canvas.getImageData throws. Leave backgroundColor alone.
    }
};

const VIRTUAL_TITLES = new Set(['free returns + package protection', 'processing fee']);
const isVirtual = (title) => VIRTUAL_TITLES.has((title || '').toLowerCase());

/**
 * Canonical bundle-building logic. All views (OrderDetail, Pressing, Packing,
 * OrderPrintCard) MUST use this so they produce identical bundles.
 *
 * Linking mechanisms (in precedence order):
 *   1a. Shopify native `lineItemGroup.id` — required components sold as
 *       a bundle product. The parent is the item whose sku === group.variantSku.
 *   1b. `_soccer_parent_line_item_key` custom attribute — Team Portal
 *       optional add-ons. Points to the parent's `_soccer_key`.
 *   1c. `_soccer_line_item_group` — Team Portal edited (":A") orders where
 *       lineItemGroup is null. Parent has `_soccer_key !== _soccer_line_item_group`;
 *       children have no `_soccer_key`. Standalone items have both attrs equal.
 *   1d. "Parent product" attribute (fallback) — value = parent SKU. Only used
 *       when the item isn't already linked via 1b or 1c.
 *   1e. `_Required SKU <child_sku>` custom-attribute KEYS — FixBrokenOrders.py
 *       pattern. STRICTLY gated on the order being tagged "Broken Order".
 *       Parent has any number of `_Required SKU <SKU>` keys; each names a
 *       child line item with sku === <SKU>. Children carry NO back-link.
 *       Unlike 1a–1d, the same child line item MAY be referenced by multiple
 *       parents (one LOGO line at qty=8 covering parent A qty=3 + parent B
 *       qty=5). Pass `tags` in the options arg to enable.
 *
 * Global invariants:
 *   - Every child attaches to exactly ONE parent (first-in-iteration-order wins).
 *     EXCEPTION: Pass 1e (Broken Order) allows a child to attach to multiple
 *     parents so each parent unit can flat-map its own embellishment instance.
 *   - Children within a bundle are deduped by line-item id.
 *   - Output order matches the original line-item order (parents appear first).
 *
 * Input:
 *   - lineItems: array of line item nodes (already de-virtualized by caller).
 *   - options.tags: order tags array (string[]); needed to activate Pass 1e.
 * Output: [{ gid, parent, children, groupData }]
 *   - gid: unique bundle id — Shopify lineItemGroup.id or `__solo__<lineItemId>`
 *   - parent: the anchor line item (typically the garment)
 *   - children: linked line items (additional components + optional add-ons)
 *   - groupData: the Shopify lineItemGroup node or null
 *
 * Callers can apply their own filters (e.g. "has both an embellishment and a
 * non-embellishment") and flatMap by quantity on top of this result.
 */
export function buildBundles(lineItems, options = {}) {
    if (!lineItems || lineItems.length === 0) return [];
    const tagsLower = new Set(((options && options.tags) || []).map(t => (t || '').trim().toLowerCase()));
    const isBrokenOrder = tagsLower.has('broken order');

    // Build _soccer_key → lineItem map and sku → lineItem map
    const soccerKeyMap = {};
    const skuMap = {};
    for (const li of lineItems) {
        const sk = (li.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
        if (sk) soccerKeyMap[sk] = li;
        if (li.sku) skuMap[li.sku] = li;
    }

    // Pass 1a: Shopify native lineItemGroup.id
    const groupMap = {};
    for (const li of lineItems) {
        const grp = li.lineItemGroup;
        if (!grp) continue;
        if (!groupMap[grp.id]) groupMap[grp.id] = { item: null, children: [], groupData: grp };
        if (grp.variantSku && li.sku === grp.variantSku) {
            groupMap[grp.id].item = li;
        } else {
            groupMap[grp.id].children.push(li);
        }
    }
    // Fallback parent detection — promote first non-embellishment child to
    // parent when the exact-match Pass 1a leaves a group with no parent. Two
    // common patterns:
    //   (a) Group's `variantSku` is a synthetic "kit" SKU that doesn't match
    //       any line item (e.g. group variantSku = NIKE-IR4022-010-SURF-XL,
    //       parent line item sku = NIKE-IR4022-010-XL). Canonical: SURF-66837.
    //   (b) Group's `variantSku` is null/missing entirely but the group has a
    //       mix of embellishment + non-embellishment members — still a real
    //       bundle. Same canonical order: SURF-66837 (Logos + garment, no
    //       variantSku set).
    // Mirrors recache_stale._extract_meta Pass 1a fallback in the backend.
    // GUARD against SP-251615 (qty-split of one purchase into two LIs with the
    // same SKU and no embellishments): require the group to have at least one
    // embellishment-type AND one non-embellishment-type member before
    // promoting. SP-251615's case has all members of the same type, so it
    // still falls through to the solo branch as before.
    const EMBELLISHMENT_TYPES_LOCAL = new Set([
        'Name', 'Number', 'Logo', 'LOGO', 'Embroidered Logo',
        'Embroidered Number', 'Crest', 'Decals',
    ]);
    const liProductType = (c) => ((c.variant?.product?.productType) || c.productType || '').trim();
    for (const gid of Object.keys(groupMap)) {
        const g = groupMap[gid];
        if (g.item) continue;
        const hasEmbell = g.children.some(c => EMBELLISHMENT_TYPES_LOCAL.has(liProductType(c)));
        const nonEmbellIdx = g.children.findIndex(c => {
            const pt = liProductType(c);
            return pt && !EMBELLISHMENT_TYPES_LOCAL.has(pt);
        });
        if (nonEmbellIdx < 0) continue;
        if (!g.groupData?.variantSku && !hasEmbell) continue; // SP-251615 guard
        // 2026-09-28 (Eric, SP-373105): a "16 Pack Case" group whose members
        // are ALL the same physical SKU (Shopify split one purchase of 16
        // cold compresses into lines of 11 and 5 with different unit prices)
        // is a quantity split, not a bundle — promoting the 11 line made the
        // 5 line a child, which the print card renders without a quantity,
        // so the ticket said 11 and counted 12 pieces instead of 17. Leave
        // such groups to the solo branch: two ordinary rows, 11 and 5.
        const memberSkus = new Set(g.children.map(c => (c.sku || '').trim()).filter(Boolean));
        if (!hasEmbell && memberSkus.size === 1 && g.children.length > 1) continue;
        g.item = g.children[nonEmbellIdx];
        g.children = g.children.filter((_, i) => i !== nonEmbellIdx);
    }
    // A Shopify lineItemGroup is only a real bundle when we found a parent.
    // Drop the rest so Pass 2 falls through to the solo branch.
    for (const gid of Object.keys(groupMap)) {
        if (!groupMap[gid].item) delete groupMap[gid];
    }

    // Pass 1b: _soccer_parent_line_item_key → parent's _soccer_key
    const keyChildren = {};
    const keyChildIds = new Set();
    for (const li of lineItems) {
        const parentKey = (li.customAttributes || []).find(a => a.key === '_soccer_parent_line_item_key')?.value;
        // Only claim the child when its parent is IN THIS LIST. A per-FO /
        // per-location subset can hold an optional add-on whose garment
        // sits in another fulfillment order (SP-360758: the white name
        // lettering at Tinton Falls, jersey at Teams Maryland); claiming it
        // here made the solo pass skip it and the card rendered empty.
        if (parentKey && soccerKeyMap[parentKey]) {
            if (!keyChildren[parentKey]) keyChildren[parentKey] = [];
            keyChildren[parentKey].push(li);
            keyChildIds.add(li.id);
        }
    }

    // Pass 1c: _soccer_line_item_group (Team Portal edited :A orders)
    const soccerGroupMap = {};
    for (const li of lineItems) {
        const groupVal = (li.customAttributes || []).find(a => a.key === '_soccer_line_item_group')?.value;
        if (groupVal) {
            if (!soccerGroupMap[groupVal]) soccerGroupMap[groupVal] = [];
            soccerGroupMap[groupVal].push(li);
        }
    }
    const soccerGroupChildren = {};
    const soccerGroupChildIds = new Set();
    for (const [groupVal, members] of Object.entries(soccerGroupMap)) {
        if (members.length <= 1) continue; // standalone group of one
        const parent = members.find(li => {
            const sk = (li.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
            return sk && sk !== groupVal;
        });
        if (!parent) continue;
        const parentSk = (parent.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
        const children = members.filter(li => li.id !== parent.id);
        for (const c of children) {
            if (keyChildIds.has(c.id)) continue; // already linked via 1b
            if (!soccerGroupChildren[parentSk]) soccerGroupChildren[parentSk] = [];
            soccerGroupChildren[parentSk].push(c);
            soccerGroupChildIds.add(c.id);
        }
    }

    // Pass 1d: "Parent product" attribute → parent SKU (fallback only).
    // Skip items already linked via Pass 1b or 1c — their explicit parent
    // linkage is authoritative and SKU-based matching is ambiguous when the
    // same SKU appears on multiple line items (two players with the same pant).
    const skuChildren = {};
    const skuChildIds = new Set();
    for (const li of lineItems) {
        if (keyChildIds.has(li.id) || soccerGroupChildIds.has(li.id)) continue;
        const parentSku = (li.customAttributes || []).find(a => a.key === 'Parent product')?.value;
        if (parentSku && skuMap[parentSku]) {
            if (!skuChildren[parentSku]) skuChildren[parentSku] = [];
            skuChildren[parentSku].push(li);
            skuChildIds.add(li.id);
        }
    }

    // Pass 1e: FixBrokenOrders.py pattern. STRICTLY gated on "Broken Order"
    // tag — never run on untagged orders even when _Required SKU keys exist.
    // Parents have `_Required SKU <child_sku>` KEYS in customAttributes; the
    // matching child line item is found by top-level lineItem.sku.
    // Children may be claimed by multiple parents (a single LOGO line at
    // qty=8 can cover several parent line items in the broken-order pattern).
    // Missing children (no line item with the required SKU) are skipped
    // silently — the bundle still renders with whatever children DO match.
    // Pass 1e is recorded as a parent-SKU → child-lineItems map keyed by
    // parent line-item id (NOT SKU) because two different parent line items
    // can share a SKU (e.g. NIKE-BV6857-729-M appears twice for two players).
    const brokenParentChildren = {};       // parent.id -> [child lineItems]
    const brokenParentIds = new Set();     // line-item ids that are broken-order parents
    if (isBrokenOrder) {
        for (const li of lineItems) {
            const reqSkus = [];
            for (const a of (li.customAttributes || [])) {
                const k = a.key || '';
                if (k.startsWith('_Required SKU ')) {
                    const csku = k.slice('_Required SKU '.length).trim();
                    if (csku) reqSkus.push(csku);
                }
            }
            if (reqSkus.length === 0) continue;
            brokenParentIds.add(li.id);
            const childrenForThisParent = [];
            for (const csku of reqSkus) {
                const child = skuMap[csku];
                if (!child) continue; // skip silently per spec
                childrenForThisParent.push(child);
            }
            brokenParentChildren[li.id] = childrenForThisParent;
        }
    }

    // Broken-order children that are NOT also a parent themselves should be
    // hidden from the solo-bundle pass — otherwise the press portal would
    // show them as standalone embellishment cards in addition to their
    // appearance under each parent.
    //
    // 2026-06-18: also hide LIs whose SKU matches ANY broken-order child SKU,
    // not just the specific LIs Pass 1e picked up. Background: skuMap (above)
    // stores only the FIRST LI per SKU, so when FixBrokenOrders.py runs more
    // than once and adds DUPLICATE LIs for the same child SKU (e.g.
    // LOGO-MW-WL-717 added in batch 1 at qty=2 AND batch 2 at qty=2 to cover
    // both player 246 + player 487's parents), the second batch's LI never
    // gets claimed by Pass 1e and falls through to the solo branch — showing
    // up at the bottom of the page as orphan embellishment cards. SURF-73338
    // canonical case (2026-06-18): 5 duplicate child LIs rendered alongside
    // their already-bundled counterparts. By hiding every LI whose SKU is in
    // the broken-order child universe, the duplicates collapse into the
    // bundle treatment without breaking single-LI orders.
    const brokenChildSkus = new Set();
    if (isBrokenOrder) {
        for (const li of lineItems) {
            for (const a of (li.customAttributes || [])) {
                const k = a.key || '';
                if (k.startsWith('_Required SKU ')) {
                    const csku = k.slice('_Required SKU '.length).trim();
                    if (csku) brokenChildSkus.add(csku);
                }
            }
        }
    }
    const brokenChildOnlyIds = new Set();
    if (isBrokenOrder) {
        for (const children of Object.values(brokenParentChildren)) {
            for (const c of children) {
                if (!brokenParentIds.has(c.id)) brokenChildOnlyIds.add(c.id);
            }
        }
        // Sweep every LI whose SKU is a known broken-order child SKU.
        for (const li of lineItems) {
            if (li.sku && brokenChildSkus.has(li.sku) && !brokenParentIds.has(li.id)) {
                brokenChildOnlyIds.add(li.id);
            }
        }
    }

    // Pass 2: assemble bundles in line-item order. Global invariant: every
    // child is attached to at most ONE parent. First parent in iteration order
    // claims the child; subsequent parents skip it.
    // EXCEPTION: Pass 1e broken-order children are NOT consumed by
    // assignedChildIds — they can attach to multiple parents.
    const result = [];
    const seenGroupIds = new Set();
    const assignedChildIds = new Set();
    const uniqueByFirstAssignment = (candidates) => {
        const out = [];
        const seen = new Set();
        for (const c of candidates) {
            if (!c || !c.id) continue;
            if (seen.has(c.id)) continue;
            if (assignedChildIds.has(c.id)) continue;
            seen.add(c.id);
            assignedChildIds.add(c.id);
            out.push(c);
        }
        return out;
    };

    for (const li of lineItems) {
        if (keyChildIds.has(li.id) || soccerGroupChildIds.has(li.id) || skuChildIds.has(li.id)) continue;
        if (brokenChildOnlyIds.has(li.id)) continue;
        const soccerKey = (li.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
        const grp = li.lineItemGroup;
        // Only use the lineItemGroup branch when Pass 1a kept the group
        // (i.e., it has a real parent). Parent-less Shopify groups get
        // dropped from groupMap above so they fall through to the solo
        // branch below — one bundle per line item.
        if (grp && groupMap[grp.id]) {
            if (seenGroupIds.has(grp.id)) continue;
            seenGroupIds.add(grp.id);
            const gd = groupMap[grp.id];
            const parent = gd.item || li;
            const nativeChildren = gd.children.filter(c => c.id !== parent.id);
            const parentSoccerKey = (parent.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
            const extraKey = (parentSoccerKey && keyChildren[parentSoccerKey]) || [];
            const extraSoccerGroup = (parentSoccerKey && soccerGroupChildren[parentSoccerKey]) || [];
            const extraSku = parent.sku ? (skuChildren[parent.sku] || []) : [];
            const children = uniqueByFirstAssignment([...nativeChildren, ...extraKey, ...extraSoccerGroup, ...extraSku]);
            result.push({ gid: grp.id, parent, children, groupData: grp });
        } else if (brokenParentIds.has(li.id)) {
            // Pass 1e branch: each broken-order parent emits its own bundle
            // with the cross-referenced children. Children are NOT consumed
            // by assignedChildIds, so the same logo line can appear under
            // many parents in this order.
            //
            // ALSO merge in Pass 1b children (`_soccer_parent_line_item_key`
            // explicit linkage) — a broken-order parent can ALSO be the
            // explicit parent of optional embellishments (e.g. a pant with
            // _Required SKU SURF-MW-WO-493 ALSO carries customer initials
            // linked via _soccer_parent_line_item_key). Without this merge,
            // optional embellishments silently drop on broken orders.
            //
            // Within-bundle dedup ONLY — do NOT call uniqueByFirstAssignment.
            // Cross-bundle dedup would prevent a SURF logo from appearing
            // under BOTH Olive's pant AND Peyton's pant when each pant
            // independently declares _Required SKU SURF-MW-WO-493.
            const soccerKey = (li.customAttributes || []).find(a => a.key === '_soccer_key')?.value;
            const extraKey = (soccerKey && keyChildren[soccerKey]) || [];
            const baseChildren = (brokenParentChildren[li.id] || []).filter(c => c.id !== li.id);
            const seen = new Set();
            const children = [];
            for (const c of [...baseChildren, ...extraKey]) {
                if (!c?.id || seen.has(c.id)) continue;
                seen.add(c.id);
                children.push(c);
            }
            result.push({ gid: `__broken__${li.id}`, parent: li, children, groupData: null });
        } else {
            const extraKey = (soccerKey && keyChildren[soccerKey]) || [];
            const extraSoccerGroup = (soccerKey && soccerGroupChildren[soccerKey]) || [];
            const extraSku = li.sku ? (skuChildren[li.sku] || []) : [];
            const children = uniqueByFirstAssignment([...extraKey, ...extraSoccerGroup, ...extraSku]);
            // Zepto Product Personalizer (2026-08-31): a solo line item with
            // `_pplr_preview` gets ONE synthetic embellishment child (the
            // logo combo) so it renders/press-tracks as a bundle.
            if (isZeptoLineItem(li)) {
                const zc = zeptoChildLineItem(li);
                if (zc) children.push(zc);
            }
            result.push({ gid: `__solo__${li.id}`, parent: li, children, groupData: null });
        }
    }

    return result;
}

// Bundle recovery for Shopify-mis-allocated embellishments.
//
// When a bundle has shipped in waves and Shopify silently zeros out the
// embellishment LI's remainingQuantity before the last garment ships, the
// orphan parent surfaces alone and `getPressingItems` drops the bundle
// entirely (no embellishment in the visible group). The presser never sees
// it → garment ships un-embellished → mis-ship breaks the bundle.
//
// `augmentBundleSiblings` rescues these by pulling the missing siblings
// back in from `allLineItems`, scaled to the parent's still-remaining qty
// (so the presser sees 1 press instance to do, not 7), and flagged with
// `_shopifyMisreportedShipped` so the UI can paint a warning chip.
export function augmentBundleSiblings(relevant, allLineItems, remainingByLiId) {
    const inSet = new Set(relevant.map(li => li.id));
    // Bundle groups represented in `relevant` plus the highest remaining qty
    // across their members (presumably the parent's, since embellishments are
    // the ones with remaining=0 in the broken state).
    const remainingByGid = {};
    for (const li of relevant) {
        const gid = li.lineItemGroup?.id;
        if (!gid) continue;
        const rem = remainingByLiId[li.id] ?? 0;
        if (!(gid in remainingByGid) || rem > remainingByGid[gid]) {
            remainingByGid[gid] = rem;
        }
    }
    const out = [...relevant];
    for (const li of allLineItems) {
        if (inSet.has(li.id)) continue;
        const gid = li.lineItemGroup?.id;
        if (!gid || !(gid in remainingByGid)) continue;
        const inheritedQty = remainingByGid[gid] || 1;
        out.push({
            ...li,
            quantity: inheritedQty,
            currentQuantity: inheritedQty,
            _shopifyMisreportedShipped: true,
        });
    }
    return out;
}

// Build pressing bundles from a raw order. Uses `buildBundles` for the core
// grouping, then applies pressing-specific filters:
//   - Only items in OPEN/IN_PROGRESS fulfillment orders at the target location
//   - Only bundles that have at least one embellishment AND at least one garment
//   - Flat-mapped by quantity (qty > 1 produces one visual bundle per instance)
export function getPressingItems(order, shopifyLocationId) {
    if (!order) return [];

    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    // Strict location match for presser sessions (which always carry a
    // shopifyLocationId). When empty/null — admin viewing directly without
    // emulating — fall back to "all OPEN/IN_PROGRESS FOs" so the bundles
    // surface regardless of location.
    const locGid = shopifyLocationId ? `gid://shopify/Location/${shopifyLocationId}` : null;
    const openFoLineItemIds = new Set();
    const remainingByLiId = {};
    for (const fo of fos) {
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (locGid && assignedId !== locGid) continue;
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        for (const e of (fo.lineItems?.edges || [])) {
            const id = e.node.lineItem?.id;
            const rem = e.node.remainingQuantity || 0;
            if (id && rem > 0) {
                openFoLineItemIds.add(id);
                remainingByLiId[id] = (remainingByLiId[id] || 0) + rem;
            }
        }
    }

    const allLineItems = (order.lineItems?.edges || []).map(e => e.node)
        .filter(li => !isVirtual(li.title))
        // Refund-aware gate: exclude lines whose currentQuantity dropped to 0
        // (fully refunded or per-line-cancelled). currentQuantity falls back to
        // quantity when the backend hasn't normalized it yet.
        .filter(li => (li.currentQuantity ?? li.quantity ?? 0) > 0);
    // Augment with bundle siblings whose remainingQuantity is 0 (Shopify
    // silently marked them fulfilled). Scaled to the parent's remaining
    // qty so only the still-needed instances render — see helper docs.
    const relevant = augmentBundleSiblings(
        allLineItems.filter(li => openFoLineItemIds.has(li.id)),
        allLineItems,
        remainingByLiId
    );

    // Core grouping (shared with OrderDetail / OrderPrintCard / QCPortal).
    const bundles = buildBundles(relevant, { tags: order.tags });

    return bundles
        .map(b => ({ ...b, items: [b.parent, ...b.children] }))
        .filter(g =>
            g.items.some(li => EMBELLISHMENT_TYPES.has(li.productType)) &&
            g.items.some(li => !EMBELLISHMENT_TYPES.has(li.productType))
        )
        .flatMap(g => {
            const qty = g.items[0]?.quantity || 1;
            // Each instance gets a UNIQUE gid so the board can track them as
            // independent cards (otherwise qty > 1 would collapse into one
            // state entry and pressing one would move them all).
            return Array.from({ length: qty }, (_, i) => ({
                ...g,
                gid: qty > 1 ? `${g.gid}__inst${i + 1}` : g.gid,
                instanceIndex: i + 1,
                totalInstances: qty,
            }));
        });
}

// Reference-only items: line items that show up in the same OPEN/IN_PROGRESS
// FOs at this location, but DON'T need pressing. Specifically: bundles with no
// embellishment line items (e.g., a stand-alone garment, accessory, or socks).
// These are surfaced at the bottom of the unpressed column purely as reference
// so a presser sees the full order contents but knows what to ignore. They
// never count toward the pressing percentage and never get a state entry.
export function getReferenceOnlyItems(order, shopifyLocationId) {
    if (!order) return [];
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    // See getPressingItems for the empty-shopifyLocationId fallback rationale.
    const locGid = shopifyLocationId ? `gid://shopify/Location/${shopifyLocationId}` : null;
    const openFoLineItemIds = new Set();
    const remainingByLiId = {};
    for (const fo of fos) {
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (locGid && assignedId !== locGid) continue;
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        for (const e of (fo.lineItems?.edges || [])) {
            const id = e.node.lineItem?.id;
            const rem = e.node.remainingQuantity || 0;
            if (id && rem > 0) {
                openFoLineItemIds.add(id);
                remainingByLiId[id] = (remainingByLiId[id] || 0) + rem;
            }
        }
    }
    const allLineItems = (order.lineItems?.edges || []).map(e => e.node)
        .filter(li => !isVirtual(li.title))
        // Refund-aware gate: exclude lines whose currentQuantity dropped to 0
        // (fully refunded or per-line-cancelled). currentQuantity falls back to
        // quantity when the backend hasn't normalized it yet.
        .filter(li => (li.currentQuantity ?? li.quantity ?? 0) > 0);
    // Augment with bundle siblings whose Shopify remainingQuantity is 0 so
    // an orphan-parent bundle gets pulled BACK into the pressing flow
    // (via getPressingItems) and stops surfacing here as reference-only.
    const relevant = augmentBundleSiblings(
        allLineItems.filter(li => openFoLineItemIds.has(li.id)),
        allLineItems,
        remainingByLiId
    );

    const bundles = buildBundles(relevant, { tags: order.tags });

    return bundles
        .map(b => ({ ...b, items: [b.parent, ...b.children] }))
        // Keep bundles that have a non-embellishment item AND no
        // embellishment items — i.e., a standalone garment/accessory.
        // (Bundles that have at least one embellishment go through
        // getPressingItems instead.) Pure-embellishment-only bundles
        // are dropped — they're meaningless without a base garment.
        .filter(g =>
            g.items.some(li => !EMBELLISHMENT_TYPES.has(li.productType)) &&
            !g.items.some(li => EMBELLISHMENT_TYPES.has(li.productType))
        )
        .flatMap(g => {
            const qty = g.items[0]?.quantity || 1;
            return Array.from({ length: qty }, (_, i) => ({
                ...g,
                gid: qty > 1 ? `${g.gid}__refinst${i + 1}` : `${g.gid}__ref`,
                instanceIndex: i + 1,
                totalInstances: qty,
                _referenceOnly: true,
            }));
        });
}

export function getAttr(attrs, ...keys) {
    if (!attrs) return null;
    for (const key of keys) {
        const found = attrs.find(a => a.key === key || a.key.toLowerCase() === key.toLowerCase());
        if (found?.value) return found.value;
    }
    return null;
}

export function getGroupProductType(group) {
    const parent = group.items.find(li =>
        li.sku && group.groupData?.variantSku && li.sku === group.groupData.variantSku
    ) || group.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items[0];
    return parent?.productType || parent?.title || '';
}

export function getGroupPlayerName(group) {
    const groupAttrs   = group.groupData?.customAttributes || [];
    const allItemAttrs = group.items.flatMap(li => li.customAttributes || []);
    return getAttr(groupAttrs, 'Player Name', 'player_name')
        || getAttr(allItemAttrs, 'Player Name', 'player_name')
        || '';
}

export function getGroupPlayerNumber(group) {
    const groupAttrs   = group.groupData?.customAttributes || [];
    const allItemAttrs = group.items.flatMap(li => li.customAttributes || []);
    return getAttr(groupAttrs, 'Number', 'player_number', '#')
        || getAttr(allItemAttrs, 'Number', 'player_number', '#')
        || '';
}

export function getGroupTeamName(group) {
    const groupAttrs   = group.groupData?.customAttributes || [];
    const allItemAttrs = group.items.flatMap(li => li.customAttributes || []);
    return getAttr(groupAttrs, 'Team', 'Team Name', 'team', 'team_name')
        || getAttr(allItemAttrs, 'Team', 'Team Name', 'team', 'team_name')
        || '';
}

// Read the flattened pressing instructions the backend attached to a line item.
// Returns { title, temp, time, pressure, peel } — every value is a string or null.
export function getLineItemRecipe(lineItem) {
    const pi = lineItem?.pressingInstructions;
    if (!pi) return { title: null, temp: null, time: null, pressure: null, peel: null };
    return {
        title:    pi.title    || null,
        temp:     pi.temp     || null,
        time:     pi.time     || null,
        pressure: pi.pressure || null,
        peel:     pi.peel     || null,
    };
}

/**
 * Compute per-bundle pick/press state from `portalState`.
 *
 * IMPORTANT: `groups` must be in original Shopify line-item order (not
 * display-sorted order) so that SKU instance indices match those used by
 * the Picking Portal. Sort for display AFTER calling this function.
 *
 * Returns an array parallel to `groups`, each element:
 *   { groupKey, skuInstanceIndex, isUnpicked, isPressed, embPickStates, anyUnpicked }
 *
 * Each `embPickStates` entry is { isUnpicked, embSku, embIdx, lineItem }.
 * (The `lineItem` reference is new vs. ItemCycler.bundlePickState — the
 * board needs it to render embellishment details in cards.)
 */
export function computeBundleStates(groups, portalState) {
    const skuCounts = {};
    const countSku = (sku, fallbackId) => {
        const key = sku || `__no_sku__${fallbackId}`;
        skuCounts[key] = (skuCounts[key] || 0) + 1;
        return skuCounts[key];
    };

    const anyPickingDone = Object.values(portalState).some(s => (s.qty_picked || 0) > 0);

    return groups.map(g => {
        const parentItem = g.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || g.items[0];
        const sku = parentItem?.sku || '';
        const groupKey = sku || `__solo__${g.gid}`;
        const skuInstanceIndex = countSku(sku, g.gid);
        const state = portalState[groupKey];
        const qtyPicked  = state?.qty_picked  ?? null;
        const qtyPressed = state?.qty_pressed ?? null;
        const isUnpicked = anyPickingDone && qtyPicked !== null && skuInstanceIndex > qtyPicked;
        // Per-instance press state (preferred): the press_instances JSONB map
        // tracks pressed/unpressed at the bundle-instance level so pressing
        // bundle #2 doesn't bleed into bundle #1. When the map is populated,
        // trust it exclusively. For legacy rows (empty map but qty_pressed>0
        // from before the schema migration) fall back to the old scalar
        // semantics so existing in-progress orders don't lose their state.
        const pressInstances = state?.press_instances || {};
        const hasInstanceMap = Object.keys(pressInstances).length > 0;
        const isPressed = hasInstanceMap
            ? !!pressInstances[String(skuInstanceIndex)]?.pressed
            : (qtyPressed !== null && skuInstanceIndex <= qtyPressed);
        // Has the parent garment been picked for this specific instance?
        const parentPicked = qtyPicked !== null && skuInstanceIndex <= qtyPicked;

        const embs = g.items.filter(li => EMBELLISHMENT_TYPES.has(li.productType));
        const embPickStates = embs.map(li => {
            const embSku = li.sku || '';
            const embIdx = countSku(embSku, li.id);
            const embQtyPicked = embSku ? (portalState[embSku]?.qty_picked ?? null) : null;
            const wasPicked = embQtyPicked !== null && embIdx <= embQtyPicked;
            return {
                isUnpicked: anyPickingDone && embQtyPicked !== null && embIdx > embQtyPicked,
                wasPicked,
                embSku, embIdx,
                lineItem: li,
            };
        });

        const anyUnpicked = isUnpicked || embPickStates.some(e => e.isUnpicked);

        // Pick status for the card badge. Differentiates not-picked vs partial
        // vs fully picked. Shown even when NOTHING in the order has been picked
        // yet — a fresh order shows "Not Picked" on every card by design.
        const pickedCount = (parentPicked ? 1 : 0) + embPickStates.filter(e => e.wasPicked).length;
        const totalCount  = 1 + embPickStates.length;
        let pickStatus;
        if (pickedCount === totalCount) pickStatus = 'picked';
        else if (pickedCount === 0)     pickStatus = 'not-picked';
        else                             pickStatus = 'partial';
        return { groupKey, skuInstanceIndex, isUnpicked, isPressed, embPickStates, anyUnpicked, pickStatus };
    });
}
